import { useTranslation } from "react-i18next";
import {
	CHART_DATA,
	CHART_INFO,
	CHART_TYPE,
} from "../../../../models/pages/biometrics/biometrics-charts.model";
import { Formatter } from "../../../../services/formatter/formatter.service";
import { Biometric, BiometricType } from "../../../../models/entities/biometrics.model";
import { BiometricsHelper } from "../../../../services/biometrics/biometrics-helper.service";

const BiometricsChartsChartSummary = ({
	type,
	chartData,
	biometrics,
}: {
	type: CHART_TYPE;
	chartData: CHART_DATA;
	biometrics: Map<string, Biometric[]>;
}) => {
	const { t } = useTranslation("common");

	let result: CHART_INFO = {
		count: null,
		min: null,
		avg: null,
		max: null,
		moderate: null,
		critical: null,
	};
	const getSeverityCounts = (type: CHART_TYPE, biometrics: Map<string, Biometric[]>) => {
		if (type === CHART_TYPE.WEIGHT) {
			return { moderate: null, critical: null };
		}

		return {
			moderate: BiometricsHelper.countModerate(biometrics.get(type) || []),
			critical: BiometricsHelper.countCritical(biometrics.get(type) || []),
		};
	};

	switch (type) {
		case CHART_TYPE.BLOOD_PRESSURE: {
			// Filter datasets to only include actual blood pressure readings, not healthy zones or trendlines
			const systolicDatasets = chartData.datasets.filter(
				(dataset) =>
					dataset.label &&
					dataset.label.toLowerCase().includes("systolic") &&
					!dataset.label.includes("Healthy Zone") &&
					!dataset.label.includes("Trendline") &&
					dataset.data.length > 0
			);

			const diastolicDatasets = chartData.datasets.filter(
				(dataset) =>
					dataset.label &&
					dataset.label.toLowerCase().includes("diastolic") &&
					!dataset.label.includes("Healthy Zone") &&
					!dataset.label.includes("Trendline") &&
					dataset.data.length > 0
			);

			// Calculate average: Systolic
			let avgSystolic = null;
			if (systolicDatasets.length > 0 && systolicDatasets[0]?.data.length) {
				avgSystolic = systolicDatasets[0].data.reduce((acc, num) => acc + num.y, 0);
				avgSystolic = Math.round(avgSystolic / systolicDatasets[0].data.length);
			}

			// Calculate average: Diastolic
			let avgDiastolic = null;
			if (diastolicDatasets.length > 0 && diastolicDatasets[0]?.data.length) {
				avgDiastolic = diastolicDatasets[0].data.reduce((acc, num) => acc + num.y, 0);
				avgDiastolic = Math.round(avgDiastolic / diastolicDatasets[0].data.length);
			}

			/* Calculate minimum */
			const minSystolicReading =
				systolicDatasets.length > 0 && systolicDatasets[0].data.length > 0
					? systolicDatasets[0].data.reduce(
							(prev, curr) => (prev.y < curr.y ? prev : curr),
							systolicDatasets[0].data[0]
						)
					: null;

			// Get the associated Diastolic value for that reading
			const minDiastolic = minSystolicReading
				? biometrics
						.get(BiometricType.blood_pressure)
						?.find(
							(x) =>
								new Date(x.vitalTime).getTime() ===
								(minSystolicReading.x as Date).getTime()
						)
						?.entries.find((x) => x.subType === BiometricType.blood_pressure_diastolic)
						?.value
				: null;

			/* Calculate maximum */
			const maxSystolicReading =
				systolicDatasets.length > 0 && systolicDatasets[0].data.length > 0
					? systolicDatasets[0].data.reduce(
							(prev, curr) => (prev.y > curr.y ? prev : curr),
							systolicDatasets[0].data[0]
						)
					: null;

			// Get the associated Diastolic value for that reading
			const maxDiastolic = maxSystolicReading
				? biometrics
						.get(BiometricType.blood_pressure)
						?.find(
							(x) =>
								new Date(x.vitalTime).getTime() ===
								(maxSystolicReading.x as Date).getTime()
						)
						?.entries.find((x) => x.subType === BiometricType.blood_pressure_diastolic)
						?.value
				: null;

			let count: number | null = null;
			if (systolicDatasets.length > 0 && diastolicDatasets.length > 0) {
				count = Math.max(
					systolicDatasets[0]?.data.length || 0,
					diastolicDatasets[0]?.data.length || 0
				);
			} else if (systolicDatasets.length > 0) {
				count = systolicDatasets[0]?.data.length || 0;
			} else if (diastolicDatasets.length > 0) {
				count = diastolicDatasets[0]?.data.length || 0;
			} else {
				count = null;
			}

			// Update state
			result = {
				count: count,
				min: minSystolicReading
					? `${minSystolicReading.y ?? Formatter.NO_DATA}/${minDiastolic ?? Formatter.NO_DATA}`
					: null,
				avg: `${avgSystolic ?? Formatter.NO_DATA}/${avgDiastolic ?? Formatter.NO_DATA}`,
				max: maxSystolicReading
					? `${maxSystolicReading.y ?? Formatter.NO_DATA}/${maxDiastolic ?? Formatter.NO_DATA}`
					: null,
				...getSeverityCounts(type, biometrics),
			};
			break;
		}
		case CHART_TYPE.GLUCOSE: {
			// Calculate values (min, max, average) across all datasets
			let min: number | null = null;
			let max: number | null = null;
			let totalSum = 0;
			let totalCount = 0;

			// Process only datasets that contain actual glucose readings (mg/dL) and have data
			const dataDatasets = chartData.datasets.filter(
				(dataset) =>
					dataset.label && dataset.label.includes("(mg/dL)") && dataset.data.length > 0
			);

			dataDatasets.forEach((dataset) => {
				dataset.data.forEach((val) => {
					if (min === null || val.y < min) min = val.y;
					if (max === null || val.y > max) max = val.y;
					totalSum += val.y;
					totalCount++;
				});
			});

			const avgGlucose = totalCount > 0 ? Math.round(totalSum / totalCount) : null;

			// Update state
			result = {
				count: totalCount,
				min: min,
				avg: avgGlucose,
				max: max,
				...getSeverityCounts(type, biometrics),
			};
			break;
		}
		case CHART_TYPE.WEIGHT: {
			// Calculate values (min, max, average)
			let min = chartData.datasets[0].data[0].y;
			let max = chartData.datasets[0].data[0].y;
			let avg = 0;
			chartData.datasets[0].data.forEach((val) => {
				if (val.y < min) min = val.y;
				if (val.y > max) max = val.y;
				avg = avg + val.y;
			});
			avg = Math.round((avg / chartData.datasets[0].data.length) * 10) / 10;

			// Update state
			result = {
				count: chartData.datasets[0].data.length,
				min,
				avg,
				max,
				...getSeverityCounts(type, biometrics),
			};
			break;
		}
		default:
			break;
	}
	return (
		<div className="d-flex align-items-baseline">
			<span className="text-bold fs-5 me-3">
				{t("PARTICIPANT.BIOMETRICS.CHARTS.TITLE_SUMMARY")}
			</span>
			<div className="chart-info-container">
				<span className="chart-info-container">
					{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_COUNT", {
						value: result.count ?? Formatter.NO_DATA,
					})}
				</span>
				<span className="chart-info-container">
					{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_MIN", {
						value: result.min ?? Formatter.NO_DATA,
					})}
				</span>
				<span className="chart-info-container">
					{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_AVG", {
						value: result.avg ?? Formatter.NO_DATA,
					})}
				</span>
				<span className="chart-info-container">
					{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_MAX", {
						value: result.max ?? Formatter.NO_DATA,
					})}
				</span>
				{/* Only show severity and moderate counts for non-weight charts */}
				{type !== CHART_TYPE.WEIGHT && (
					<>
						<span className="chart-info-container">
							{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_MODERATE", {
								value: result.moderate ?? Formatter.NO_DATA,
							})}
						</span>
						<span className="chart-info-container">
							{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_CRITICAL", {
								value: result.critical ?? Formatter.NO_DATA,
							})}
						</span>
					</>
				)}
			</div>
		</div>
	);
};

export default BiometricsChartsChartSummary;
